/**
 * Payment diagnostics — passive card-swipe telemetry.
 *
 * WHY THIS EXISTS
 * A failed card swipe leaves no trace anywhere. Payrix only records transactions
 * that succeeded, so a reader that fails to produce a readable track creates no
 * transaction, no error and no log line — which is why swipe problems have only
 * ever been diagnosable by asking an operator what they saw. This module records
 * the swipe lifecycle from the browser so the failures become countable.
 *
 * It is entirely passive: no UI, nothing rendered, nothing awaited on the payment
 * path. Every hook is wrapped so a diagnostics failure can never affect a payment.
 *
 * OFF BY DEFAULT. Only runs when Payment Settings -> "Turn on payment diagnostics"
 * is enabled for the county (paymentConfig.paymentDiagnostics === true).
 *
 * WHAT IT CAPTURES
 * When it is on, it captures everything it can reach, rather than a hand-picked
 * list of fields. The first version kept only a whitelist of field names, which
 * meant the Payrix failure payload — the one message that says why a swipe was
 * rejected — was reduced to its type name and discarded. Anything Payrix sends,
 * any error our own dialog raises, the focus and visibility transitions during
 * the swipe window, and the state of the PayFields iframes are all recorded.
 *
 * CARDHOLDER DATA — the one thing deliberately NOT captured
 * Everything above passes through `sanitize` first, and the same rules run again
 * server-side as defence in depth. Three separate mechanisms:
 *   1. Key names. A key that looks like it holds card data (number, pan, cvv,
 *      track, expiry, magstripe...) has its VALUE dropped and is recorded as a
 *      descriptor — the key's presence, its type and its length — never content.
 *   2. String values. Track patterns and PAN-shaped digit runs are replaced.
 *   3. Numbers. A 12-19 digit integer is a PAN shape and is replaced, because a
 *      PAN arriving as a JSON number would otherwise bypass string scrubbing.
 * The keystroke capture (which exists to detect a reader firing into the wrong
 * window) records counts, timings, sentinel positions and a per-class histogram.
 * It never stores, accumulates or transmits the characters themselves.
 */
import { clients } from '../clients';

const { backendRest } = clients.direct;

const MAX_EVENTS = 500;
const MAX_STRING = 300;
const MAX_ARRAY = 25;
const MAX_KEYS = 40;
// Raised from 5 after the first production session on the new capture showed it
// truncating the fields the diagnosis turns on. A Payrix token response nests
// response.data[0].customer and .payment, and at 5 both came back '[max_depth]'
// -- including cardType and binType, which the code comments call out as the
// point. 8 reaches them with one level of headroom. Breadth is still bounded by
// MAX_KEYS/MAX_ARRAY, and key-name redaction applies at every level, so going
// deeper exposes more structure but no cardholder data.
const MAX_DEPTH = 8;
const MAX_GAPS = 40;
const PAYRIX_ORIGIN = /^https:\/\/([a-z0-9-]+\.)*payrix\.com$/i;

/**
 * Keys whose values are never kept, matched on the key NAME by SUBSTRING.
 *
 * This is the primary defence: it does not depend on recognising the shape of the
 * value, so it holds for a CVV (too short to look like a PAN), an encrypted track
 * blob, or a field Payrix adds later that we have never seen.
 *
 * Substring, not word-boundary, on purpose: a word-boundary version silently
 * failed to match `expiration`, because `expir` is followed by a letter.
 * Mirrors recordSwipeDiagnostics.js on the backend, which applies it again.
 */
const SENSITIVE_KEY = new RegExp([
  'card', 'number', 'acct', 'account', 'routing',
  'cvv', 'cvc', 'cvn', 'csc',
  'track', 'magstripe', 'magneprint', 'ksn', 'dukpt', 'swipedata', 'swipebuffer',
  'expir', 'expdate', 'expmonth', 'expyear', 'expyr',
  '^pan$', '^pan[_-]', '[_-]pan$', '^cid$',
  // Credentials. PayFields.config carries the merchant apiKey and a successful
  // tokenization returns a chargeable payment token; capturing whole payloads
  // means both now reach this function.
  'token', 'apikey', 'api[_-]?key', 'secret', 'password', 'passwd',
  'bearer', 'signature', 'privatekey', 'private[_-]?key', 'authorization',
  // PIN material. Storing a PIN block is prohibited outright, encrypted or not.
  // 'ksn' and 'dukpt' above are the key-management half; these are the PIN itself.
  'pin', 'pinblock', 'pinblk', 'pinpad', 'emv',
  // Credentials under names the list did not previously reach.
  'jwt', 'credential', 'cred', 'auth',
  // Credential keys named explicitly, plus a separated suffix. NOT a bare
  // 'key$' -- that matches altKey on a keyboard event (and monkey).
  '[_-]key$', 'apikey', 'clientkey', 'merchantkey', 'sessionkey',
  'secretkey', 'accesskey', 'signingkey', 'encryptionkey',
].join('|'), 'i');

/** Matching keys that carry no cardholder data and are worth keeping. */
const NEVER_SENSITIVE = /^(cardType|cardBrand|binType|fieldCount|numberOfFields|authorizationCode|authCode|approvalCode)$/i;

const isSensitiveKey = (k) => !NEVER_SENSITIVE.test(k) && SENSITIVE_KEY.test(k);

/**
 * Remove anything that could be cardholder data from a free-text string.
 * Order matters: track patterns first (they contain PANs), then bare digit runs.
 */
export const scrubText = (value) => {
  if (value === null || value === undefined) return null;
  let s = String(value);
  // Tracks first, because they contain a PAN and a cardholder name, and each in
  // two forms: sentinel-terminated, and UNTERMINATED. A swipe that fails to read
  // completely emits no end sentinel, and that is precisely the event this
  // feature exists to capture -- the old rules required the closing '?' and so
  // passed a partial track through verbatim.
  s = s.replace(/%[A-Za-z]?\d{10,}[^?\n]{0,110}(\?|$)/g, '[TRACK1]');
  s = s.replace(/%[^?\n]{0,120}\?/g, '[TRACK1]');
  s = s.replace(/;\d{10,}[=^][^?\n]{0,110}(\?|$)/g, '[TRACK2]');
  s = s.replace(/;[^?\n]{0,120}\?/g, '[TRACK2]');
  // PAN-shaped digit runs, with NO \b anchors and a wider separator class.
  // In a track the PAN sits directly behind the format code ('%B4111...'); 'B'
  // is a word character, so there is no word boundary before the first digit and
  // an anchored rule matches nothing. Same for 'tok_4111...' and EMV TLV hex.
  // Any non-alphanumeric may separate PAN digits, not just space/dot/dash/slash:
  // underscore, comma, colon, NBSP and double spaces all defeated the old class.
  s = s.replace(/(?:\d[^0-9A-Za-z\n]{0,2}){12,19}/g, '[REDACTED]');
  s = s.replace(/\d{12,}/g, '[REDACTED]');
  // Last resort: strip every non-alphanumeric and see whether a PAN-length digit
  // run is hiding under separators the pass above still did not anticipate.
  const stripped = s.replace(/[^0-9A-Za-z]/g, '');
  if (/\d{12,19}/.test(stripped)) {
    s = s.replace(/(?:\d[^0-9A-Za-z\n]*){12,19}/g, '[REDACTED]');
  }
  if (s.length > MAX_STRING) s = `${s.slice(0, MAX_STRING)}…`;
  return s;
};

/** A 12-19 digit integer is PAN-shaped. Applied to numbers, which scrubText never sees. */
const scrubNumber = (n) => {
  if (!Number.isFinite(n)) return null;
  const digits = String(Math.abs(Math.trunc(n)));
  if (digits.length >= 12 && digits.length <= 19) return '[REDACTED_NUMERIC]';
  return n;
};

/** Describe a dropped value without revealing it. */
const describeRedacted = (value) => {
  const t = Array.isArray(value) ? 'array' : typeof value;
  let len = null;
  try {
    if (typeof value === 'string' || Array.isArray(value)) len = value.length;
    else if (value && t === 'object') len = Object.keys(value).length;
    else if (typeof value === 'number') len = String(Math.abs(Math.trunc(value))).length;
  } catch (_) { /* no-op */ }
  return { redacted: true, type: t, length: len };
};

/**
 * Recursively sanitize an arbitrary value so it can be stored. Captures structure
 * and every non-sensitive value; bounds depth, breadth and string length so a
 * hostile or merely enormous payload cannot blow up the document.
 *
 * Keys starting with `$` or containing a dot are dropped: the event subdocument is
 * `strict: false`, and Mongo would read those as operators or paths.
 */
export const sanitize = (value, depth = 0) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return scrubText(value);
  if (typeof value === 'number') return scrubNumber(value);
  if (typeof value === 'boolean') return value;
  if (typeof value === 'function') return '[function]';
  if (depth >= MAX_DEPTH) return '[max_depth]';
  if (Array.isArray(value)) {
    return value.slice(0, MAX_ARRAY).map((v) => sanitize(v, depth + 1));
  }
  if (typeof value === 'object') {
    // Error objects carry nothing on their own keys; lift the useful parts.
    if (value instanceof Error) {
      return {
        errorName: scrubText(value.name),
        message: scrubText(value.message),
        stack: scrubText(value.stack),
      };
    }
    const out = {};
    let keys = [];
    try {
      keys = Object.keys(value);
    } catch (_) {
      return '[unreadable]';
    }
    keys
      .filter((k) => k && !k.startsWith('$') && !k.includes('.'))
      .slice(0, MAX_KEYS)
      .forEach((k) => {
        // The key itself is scrubbed and length-capped: a PAN can arrive AS a
        // key name, and nothing else here would have touched it.
        const safeKey = String(scrubText(k)).slice(0, 80);
        out[safeKey] = isSensitiveKey(k)
          ? describeRedacted(value[k])
          : sanitize(value[k], depth + 1);
      });
    return out;
  }
  return null;
};

/** Classify a character without retaining it. */
const charClass = (ch) => {
  if (ch === '%') return 'sentinel_pct';
  if (ch === ';') return 'sentinel_semi';
  if (ch === '?') return 'sentinel_end';
  if (ch === '^') return 'separator_caret';
  if (ch === '=') return 'separator_equals';
  if (/^[0-9]$/.test(ch)) return 'digit';
  if (/^[A-Za-z]$/.test(ch)) return 'alpha';
  return 'other';
};

const describeActiveElement = () => {
  try {
    const el = document.activeElement;
    if (!el) return 'none';
    const id = el.id ? `#${el.id}` : '';
    return `${el.tagName.toLowerCase()}${id}`;
  } catch (_) {
    return 'unavailable';
  }
};

/**
 * Inventory the PayFields iframes: which exist, and whether each is actually
 * visible and sized. Reads no content — these are cross-origin, and their values
 * are unreadable from here by design. Presence and geometry are what distinguish
 * "the swiper never mounted" from "it mounted and the read still failed".
 */
const iframeInventory = () => {
  try {
    const out = [];
    const nodes = document.querySelectorAll('iframe[id^="payFields-iframe"]');
    Array.prototype.slice.call(nodes, 0, 15).forEach((el) => {
      let cs = {};
      try { cs = window.getComputedStyle(el) || {}; } catch (_) { /* no-op */ }
      out.push({
        id: el.id || null,
        w: Math.round(el.offsetWidth || 0),
        h: Math.round(el.offsetHeight || 0),
        display: cs.display || null,
        visibility: cs.visibility || null,
      });
    });
    return out;
  } catch (_) {
    return null;
  }
};

/**
 * Snapshot the global PayFields object. Its own fields are cross-origin iframes so
 * no card content is reachable, but its flags (notably `swiped`) say what the SDK
 * itself believes happened. Sanitized like anything else.
 */
const payFieldsSnapshot = () => {
  try {
    const pf = window.PayFields;
    if (!pf) return { present: false };
    return {
      present: true,
      swiped: typeof pf.swiped === 'boolean' ? pf.swiped : null,
      mode: sanitize(pf.mode),
      customer: pf.customer ? '[present]' : null,
      fieldCount: Array.isArray(pf.fields) ? pf.fields.length : null,
      config: sanitize(pf.config, 2),
      button: sanitize(pf.button, 2),
    };
  } catch (_) {
    return { present: null, error: 'unreadable' };
  }
};

/** Load timings for the Payrix assets, to catch a slow or cache-missing CDN. */
const payrixResourceTimings = () => {
  try {
    if (!window.performance || !window.performance.getEntriesByType) return null;
    return window.performance.getEntriesByType('resource')
      .filter((r) => PAYRIX_ORIGIN.test((r.name || '').replace(/^(https:\/\/[^/]+).*$/, '$1')))
      .slice(0, 10)
      .map((r) => ({
        kind: r.initiatorType || null,
        ms: Math.round(r.duration || 0),
        transferred: Math.round(r.transferSize || 0),
        encoded: Math.round(r.encodedBodySize || 0),
      }));
  } catch (_) {
    return null;
  }
};

const environment = () => {
  const n = (typeof navigator !== 'undefined' && navigator) ? navigator : {};
  let conn = null;
  try {
    const c = n.connection || n.mozConnection || n.webkitConnection;
    if (c) conn = { effectiveType: c.effectiveType || null, rtt: c.rtt || null, downlink: c.downlink || null };
  } catch (_) { /* no-op */ }
  let visibility = null;
  let hasFocus = null;
  try { visibility = document.visibilityState || null; } catch (_) { /* no-op */ }
  try { hasFocus = document.hasFocus(); } catch (_) { /* no-op */ }
  return {
    platform: (n.platform || '').slice(0, 60) || null,
    language: (n.language || '').slice(0, 20) || null,
    cores: typeof n.hardwareConcurrency === 'number' ? n.hardwareConcurrency : null,
    dpr: (typeof window !== 'undefined' && window.devicePixelRatio) || null,
    connection: conn,
    visibilityAtStart: visibility,
    hadFocusAtStart: hasFocus,
  };
};

/**
 * Create a diagnostics session. When `enabled` is false every method is a no-op,
 * so callers never need to branch.
 */
export const createSwipeDiagnostics = ({
  enabled,
  paymentContext,
  payrixPaymentType,
  workstation,
  requestId,
  merchantId,
} = {}) => {
  if (!enabled) {
    const noop = () => {};
    return {
      enabled: false,
      mark: noop,
      openSwipeWindow: noop,
      closeSwipeWindow: noop,
      attach: noop,
      detach: noop,
      flush: noop,
    };
  }

  const startedAt = Date.now();
  const events = [];
  let dropped = 0;
  let flushed = false;
  let messageHandler = null;
  let keyHandler = null;
  let focusHandler = null;
  let blurHandler = null;
  let visibilityHandler = null;
  let errorHandler = null;
  let rejectionHandler = null;
  let consoleErrorOriginal = null;
  let swipeWindowOpen = false;

  // Keystroke burst state. Counters, timings and class histograms only: the
  // characters are looked at to classify them and are never stored, accumulated
  // or sent. Worst case, if a swipe window is still open while a card is keyed by
  // hand, what is recorded is a count and inter-key timings — no digits.
  let burst = null;

  const since = () => Date.now() - startedAt;

  const push = (event, fields) => {
    if (events.length >= MAX_EVENTS) {
      dropped += 1;
      return;
    }
    events.push({ at: since(), event, ...(fields || {}) });
  };

  const mark = (event, fields) => {
    try {
      // Anything a caller hands us is sanitized: callers pass raw error objects.
      push(event, fields === undefined ? undefined : sanitize(fields, 1));
    } catch (_) { /* diagnostics must never throw into the payment path */ }
  };

  const finishBurst = (reason) => {
    if (!burst) return;
    const b = burst;
    if (b.timer) clearTimeout(b.timer);
    burst = null;
    const gaps = b.gaps.slice().sort((a, z) => a - z);
    const median = gaps.length ? gaps[Math.floor(gaps.length / 2)] : null;
    push('keystrokes_on_page', {
      reason,
      keys: b.keys,
      ms: b.last - b.first,
      maxGapMs: b.maxGap,
      medianGapMs: median,
      // Full (capped) gap series: a reader emits an even fast cadence, a human
      // does not, and a reader that stalls mid-track shows a spike.
      gapsMs: b.gaps.slice(0, MAX_GAPS),
      firstCharClass: b.firstCharClass,
      // Counts per character class. Says whether a full, well-formed track
      // arrived without revealing any of it.
      classHistogram: b.hist,
      // Positions of the sentinels and separators within the burst.
      sentinelPositions: b.sentinelPositions.slice(0, MAX_GAPS),
      sawSentinel: b.sawSentinel,
      sawEnter: b.sawEnter,
      activeElementAtStart: b.activeElementAtStart,
      activeElementAtEnd: describeActiveElement(),
    });
  };

  const onKeydown = (e) => {
    try {
      const now = Date.now();
      const ch = (e.key && e.key.length === 1) ? e.key : '';
      const cls = charClass(ch);
      if (!burst) {
        burst = {
          keys: 0,
          first: now,
          last: now,
          maxGap: 0,
          gaps: [],
          hist: {},
          sentinelPositions: [],
          firstCharClass: cls,
          sawSentinel: false,
          sawEnter: false,
          activeElementAtStart: describeActiveElement(),
          timer: null,
        };
      } else {
        const gap = now - burst.last;
        burst.gaps.push(gap);
        if (gap > burst.maxGap) burst.maxGap = gap;
      }
      burst.hist[cls] = (burst.hist[cls] || 0) + 1;
      if (cls === 'sentinel_pct' || cls === 'sentinel_semi' || cls === 'sentinel_end'
          || cls === 'separator_caret' || cls === 'separator_equals') {
        burst.sentinelPositions.push({ i: burst.keys, cls });
      }
      burst.keys += 1;
      burst.last = now;
      if (ch === '%' || ch === ';') burst.sawSentinel = true;
      if (e.key === 'Enter') burst.sawEnter = true;

      // A card swipe arrives as one fast burst; close it out once input stops.
      if (burst.timer) clearTimeout(burst.timer);
      burst.timer = setTimeout(() => finishBurst('idle'), 750);
    } catch (_) { /* never throw from a DOM handler */ }
  };

  /**
   * Focus and visibility during the swipe window.
   *
   * This is aimed squarely at the reported signature where the Name field fills
   * in and nothing else does: if focus leaves the swiper iframe part-way through a
   * track, the remainder of the characters go somewhere else and the read is
   * partial. Without this there is no way to tell that from a bad reader.
   */
  const onFocusIn = (e) => {
    try {
      if (!swipeWindowOpen) return;
      let target = 'unknown';
      try {
        const el = e && e.target;
        if (el && el.tagName) target = `${el.tagName.toLowerCase()}${el.id ? `#${el.id}` : ''}`;
      } catch (_) { /* no-op */ }
      push('focus_change', { to: target, active: describeActiveElement() });
    } catch (_) { /* no-op */ }
  };

  const onWindowBlur = () => {
    try {
      if (!swipeWindowOpen) return;
      push('window_blur', { active: describeActiveElement() });
    } catch (_) { /* no-op */ }
  };

  const onVisibility = () => {
    try {
      push('visibility_change', {
        state: (document && document.visibilityState) || null,
        duringSwipeWindow: swipeWindowOpen,
      });
    } catch (_) { /* no-op */ }
  };

  /** Script errors from the Payrix bundle, which otherwise only reach the console. */
  const isPayrixRelated = (text) => {
    try {
      return /payrix|payfields|payfield/i.test(String(text || ''));
    } catch (_) {
      return false;
    }
  };

  const onWindowError = (e) => {
    try {
      const src = (e && (e.filename || (e.error && e.error.stack))) || '';
      const msg = (e && (e.message || (e.error && e.error.message))) || '';
      if (!isPayrixRelated(src) && !isPayrixRelated(msg)) return;
      push('script_error', {
        message: scrubText(msg),
        source: scrubText(src),
        line: (e && e.lineno) || null,
      });
    } catch (_) { /* no-op */ }
  };

  const onRejection = (e) => {
    try {
      const r = e && e.reason;
      const text = (r && (r.stack || r.message)) || r;
      if (!isPayrixRelated(text)) return;
      push('unhandled_rejection', { reason: sanitize(r, 2) });
    } catch (_) { /* no-op */ }
  };

  /**
   * Stop watching. Called when Payrix reports the swiper closed, so that typing
   * in the phone or email fields afterwards is never recorded as a swipe burst.
   * Declared before onMessage, which closes the window on Payrix's own signal.
   */
  const endSwipeWindow = (reason) => {
    try {
      swipeWindowOpen = false;
      if (!keyHandler) return;
      finishBurst('window_closed');
      document.removeEventListener('keydown', keyHandler, true);
      keyHandler = null;
      push('swipe_window_close', { reason, iframes: iframeInventory() });
    } catch (_) { /* no-op */ }
  };

  const onMessage = (e) => {
    try {
      if (!e.origin || !PAYRIX_ORIGIN.test(e.origin)) return;
      let d = e.data;
      if (typeof d === 'string') {
        try { d = JSON.parse(d); } catch (_) { return; }
      }
      if (!d || typeof d !== 'object' || !d.sender) return;

      const sender = String(d.sender).slice(0, 40);

      if (d.sender === 'closeSwipe') {
        // Keep the whole message, not just the flag: whatever else Payrix puts
        // alongside `swiped` is the closest thing to a reason we can get.
        push('payrix_close_swipe', {
          swiped: !!d.swiped,
          payload: sanitize(d, 1),
          iframes: iframeInventory(),
          payFields: payFieldsSnapshot(),
        });
        endSwipeWindow('payrix_close_swipe');
        return;
      }
      if (d.sender === 'error') {
        push('payrix_field_error', {
          field: d.fieldType || null,
          subField: d.subField || null,
          error: scrubText(d.error),
          details: scrubText(d.details),
          payload: sanitize(d, 1),
        });
        return;
      }
      // Everything else — 'log', 'payFieldsResponse' and anything Payrix adds
      // later — is captured whole. This is the change: the previous version
      // recorded only the sender name for these, which is why the failure
      // payload was never available.
      push(d.sender === 'log' ? 'payrix_log' : 'payrix_message', {
        sender,
        level: d.type || null,
        message: d.message === undefined ? undefined : scrubText(d.message),
        payload: sanitize(d, 1),
      });
    } catch (_) { /* never throw from a DOM handler */ }
  };

  /** Start watching for reader keystrokes that land on the page. */
  const beginSwipeWindow = (source) => {
    try {
      swipeWindowOpen = true;
      push('swipe_window_open', {
        source,
        activeElement: describeActiveElement(),
        swiperIframePresent: !!document.querySelector('#payFields-iframe-swiper'),
        iframes: iframeInventory(),
        payFields: payFieldsSnapshot(),
        visibility: (document && document.visibilityState) || null,
        hasFocus: (() => { try { return document.hasFocus(); } catch (_) { return null; } })(),
      });
      if (!keyHandler) {
        keyHandler = onKeydown;
        document.addEventListener('keydown', keyHandler, true);
      }
    } catch (_) { /* no-op */ }
  };

  return {
    enabled: true,
    mark,
    openSwipeWindow: beginSwipeWindow,
    closeSwipeWindow: endSwipeWindow,

    attach() {
      try {
        if (!messageHandler) {
          messageHandler = onMessage;
          // Capture phase so we observe the message even though PayFields also listens.
          window.addEventListener('message', messageHandler, true);
        }
        if (!focusHandler) {
          focusHandler = onFocusIn;
          document.addEventListener('focusin', focusHandler, true);
        }
        if (!blurHandler) {
          blurHandler = onWindowBlur;
          window.addEventListener('blur', blurHandler, true);
        }
        if (!visibilityHandler) {
          visibilityHandler = onVisibility;
          document.addEventListener('visibilitychange', visibilityHandler, true);
        }
        if (!errorHandler) {
          errorHandler = onWindowError;
          window.addEventListener('error', errorHandler, true);
        }
        if (!rejectionHandler) {
          rejectionHandler = onRejection;
          window.addEventListener('unhandledrejection', rejectionHandler, true);
        }
        // Payrix logs its own failures to console.error and nowhere else. Wrap it
        // for the life of the dialog, pass every call straight through, and keep
        // only the Payrix-related ones. Bounded to Payrix on purpose: hoovering
        // every app console error would collect unrelated taxpayer data.
        if (!consoleErrorOriginal && typeof console !== 'undefined' && console.error) {
          consoleErrorOriginal = console.error;
          const original = consoleErrorOriginal;
          console.error = function wrapped(...args) {
            try {
              const joined = args.map((a) => {
                try {
                  if (a instanceof Error) return `${a.name}: ${a.message}`;
                  return typeof a === 'string' ? a : JSON.stringify(a);
                } catch (_) { return ''; }
              }).join(' ');
              if (isPayrixRelated(joined)) {
                push('console_error', { args: sanitize(args, 2) });
              }
            } catch (_) { /* never break console.error */ }
            return original.apply(this, args);
          };
        }
      } catch (_) { /* no-op */ }
    },

    detach() {
      try {
        if (messageHandler) {
          window.removeEventListener('message', messageHandler, true);
          messageHandler = null;
        }
        if (focusHandler) {
          document.removeEventListener('focusin', focusHandler, true);
          focusHandler = null;
        }
        if (blurHandler) {
          window.removeEventListener('blur', blurHandler, true);
          blurHandler = null;
        }
        if (visibilityHandler) {
          document.removeEventListener('visibilitychange', visibilityHandler, true);
          visibilityHandler = null;
        }
        if (errorHandler) {
          window.removeEventListener('error', errorHandler, true);
          errorHandler = null;
        }
        if (rejectionHandler) {
          window.removeEventListener('unhandledrejection', rejectionHandler, true);
          rejectionHandler = null;
        }
        if (consoleErrorOriginal) {
          console.error = consoleErrorOriginal;
          consoleErrorOriginal = null;
        }
        if (keyHandler) {
          document.removeEventListener('keydown', keyHandler, true);
          keyHandler = null;
        }
        if (burst && burst.timer) clearTimeout(burst.timer);
        burst = null;
        swipeWindowOpen = false;
      } catch (_) { /* no-op */ }
    },

    /** Send once. Fire and forget: a failure here must never surface to the user. */
    flush(outcome) {
      try {
        if (flushed) return;
        flushed = true;
        finishBurst('flush');
        if (!events.length) return;
        backendRest.post('/tax/ccPayments/payrix/swipe-diagnostics', {
          requestId: requestId || null,
          paymentContext: paymentContext || null,
          payrixPaymentType: payrixPaymentType || null,
          workstation: workstation || null,
          merchantId: merchantId || null,
          outcome: outcome || 'unknown',
          startedAt: new Date(startedAt).toISOString(),
          durationMs: since(),
          userAgent: (navigator && navigator.userAgent ? navigator.userAgent : '').slice(0, 300),
          screen: (window.screen ? `${window.screen.width}x${window.screen.height}` : null),
          environment: environment(),
          payrixResources: payrixResourceTimings(),
          payFieldsAtFlush: payFieldsSnapshot(),
          droppedEvents: dropped,
          events,
        }).catch(() => {});
      } catch (_) { /* no-op */ }
    },
  };
};

export default { createSwipeDiagnostics, scrubText, sanitize };
